import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"; // Import routing components
import { useTranslation } from "react-i18next"; // For internationalization
import { FaBars, FaTimes } from "react-icons/fa"; // Icons for mobile menu
import SearchBar from "./SearchBar.js"; // Import the search component
import "../styles/Header.css"; // Import component styles
import { getUserLanguage } from "../utils.js"; // Utility to get user language (if needed)
import { collection, query, where, getDocs } from "firebase/firestore"; // Firestore functions
import { db } from "../firebase.js"; // Firestore instance
import { useAuth } from "./AuthProvider.js"; // Import the custom auth hook

const Header = () => {
  // State for mobile menu visibility
  const [isMobile, setIsMobile] = useState(false);
  // State to store products (likely for the search bar)
  const [products, setProducts] = useState([]);
  // Translation function
  const { t } = useTranslation();
  // Get current user data and logout function from Auth context
  const { currentUser, logoutUser } = useAuth();
  // Hook for programmatic navigation
  const navigate = useNavigate();

  // Determine user roles based on currentUser from context
  const isTeacher = currentUser?.role === 'teacher';
  const isStudent = currentUser?.role === 'student';
  const isAdmin = currentUser?.role === 'admin';

  // Effect to fetch products (for search bar) and handle window resizing
  useEffect(() => {
    const userLanguage = getUserLanguage(); // Determine language (en/pt)

    // Async function to fetch products based on language
    const fetchProducts = async () => {
      try {
        // Query products collection matching the user's language
        const productsQuery = query(
          collection(db, "products"),
          where("language", "==", userLanguage)
        );
        const querySnapshot = await getDocs(productsQuery);
        const productsData = [];
        // Map Firestore documents to product objects
        querySnapshot.forEach((doc) => {
          productsData.push({ id: doc.id, ...doc.data() });
        });
        setProducts(productsData); // Update state with fetched products
      } catch (error) {
        console.error("Header: Error fetching products:", error);
      }
    };

    fetchProducts(); // Call the fetch function

    // Event listener to close mobile menu on window resize if screen becomes large
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobile(false); // Close mobile menu on larger screens
      }
    };

    window.addEventListener("resize", handleResize);
    // Cleanup function to remove event listener when component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  // Toggle mobile menu state
  const toggleMobileMenu = () => {
    setIsMobile(!isMobile);
  };

  // Close mobile menu when a link is clicked
  const handleLinkClick = () => {
    if (isMobile) {
      setIsMobile(false);
    }
  };

  // Handle user logout
  const handleLogout = async () => {
    handleLinkClick(); // Close mobile menu if open
    await logoutUser(); // Call logout function from Auth context
    navigate("/login"); // Redirect user to login page after logout
  };


  // Render the header component
  return (
    <header className="header">
      <nav className="navbar">
        {/* Mobile Menu Icon (Hamburger/Close) */}
        <div className="menu-icon" onClick={toggleMobileMenu}>
          {isMobile ? <FaTimes /> : <FaBars />}
        </div>

        {/* Logo/Brand Name */}
        <h1 className="logo">
          <Link to="/" onClick={handleLinkClick}>YouSmart</Link> {/* Link to homepage */}
        </h1>

        {/* Navigation Links - Class changes based on mobile state */}
        <ul
          className={
            isMobile ? "nav-links nav-links-mobile active" : "nav-links"
          }
        >
          {/* --- Common Links (Visible to all) --- */}
          <li>
            <Link to="/" onClick={handleLinkClick}>
              {t("header.home")} {/* Home */}
            </Link>
          </li>
          <li>
            <Link to="/news" onClick={handleLinkClick}>
              {t("header.news")} {/* News/Articles */}
            </Link>
          </li>
          <li>
            <Link to="/enem-training" onClick={handleLinkClick}>
              {t("header.enemTraining", "ENEM: Treinar Redação")}
            </Link>
          </li>

          {(isTeacher || isAdmin) && (
            <li>
              <Link to="/manage/classrooms" onClick={handleLinkClick}>
                {t("header.classrooms", "Minhas Turmas")}
              </Link>
            </li>
          )}

          {/* Add other common links like About, Contact etc. here if needed */}


          {/* --- Student Specific Links --- */}
          {isStudent && (
            <>
              <li>
                <Link to="/student-profile" onClick={handleLinkClick}>
                  {/* Use default text if translation key not found */}
                  {t("header.myProfile", "My Profile")}
                </Link>
              </li>
               <li>
                <Link to="/my-articles" onClick={handleLinkClick}>
                  {t("header.myArticles")} {/* My Articles */}
                </Link>
              </li>
            </>
          )}

          {/* --- Teacher Specific Links --- */}
          {isTeacher && (
            <>
              <li>
                <Link to="/manage/categories" onClick={handleLinkClick}>
                   {t("header.manageCategories", "Manage Categories")}
                </Link>
              </li>
               <li>
                <Link to="/manage/students" onClick={handleLinkClick}>
                   {t("header.manageStudents", "Manage Students")}
                </Link>
              </li>
              {/* Add other teacher-specific dashboard/links here */}
            </>
          )}

          {/* --- Admin Specific Links (All Management Options) --- */}
          {isAdmin && (
            <>
              <li>
                <Link to="/manage/categories" onClick={handleLinkClick}>
                   {t("header.manageCategories", "Manage Categories")}
                </Link>
              </li>
               <li>
                <Link to="/manage/students" onClick={handleLinkClick}>
                   {t("header.manageStudents", "Manage Students")}
                </Link>
              </li>
               <li>
                <Link to="/tests/create" onClick={handleLinkClick}>
                   {t("header.createTest", "Create Test")}
                </Link>
              </li>
               <li>
                <Link to="/my-articles" onClick={handleLinkClick}>
                   {t("header.myArticles", "My Articles")}
                </Link>
              </li>
               <li>
                <Link to="/articles/create" onClick={handleLinkClick}>
                   {t("header.createArticle", "Create Article")}
                </Link>
              </li>
            </>
          )}

          {/* --- Authentication Related Links --- */}
          {currentUser ? (
            // Links shown when user IS logged in
            <>
              <li>
                {/* Display user identifier (email/name) */}
                 {isStudent ? (
                     // Link student email to their profile page
                     <Link to="/student-profile" onClick={handleLinkClick} className="user-identifier-link">
                        {currentUser.email}
                     </Link>
                 ) : (
                     // Display teacher/other role email as text (no profile link assumed yet)
                     <span className="user-identifier-text">{currentUser.email}</span>
                 )}
              </li>
              <li>
                {/* Logout Button */}
                <button onClick={handleLogout} className="logout-button">
                    {t("header.logout")} {/* Logout */}
                </button>
              </li>
            </>
          ) : (
            // Links shown when user IS NOT logged in
            <>
              <li>
                <Link to="/login" onClick={handleLinkClick}>
                  {t("header.login")} {/* Login */}
                </Link>
              </li>
              {/* Link to Student Subscription Page */}
              <li>
                 <Link to="/subscribe/student" onClick={handleLinkClick}>
                   {t("header.subscribeStudent", "Student Signup")}
                 </Link>
              </li>
               {/* Link to Teacher Subscription Page */}
              <li>
                 <Link to="/subscribe/teacher" onClick={handleLinkClick}>
                   {t("header.subscribeTeacher", "Teacher Signup")}
                 </Link>
              </li>
            </>
          )}
        </ul>

        {/* Search Bar Component */}
        <SearchBar products={products} />
      </nav>
    </header>
  );
};

export default Header;
